import React, { useState } from 'react';

interface PowerCadenceData {
  avgPower?: number;
  maxPower?: number;
  normalizedPower?: number;
  avgCadence?: number;
  maxCadence?: number;
  powerZones?: Array<{
    zone: number;
    name: string;
    minWatts: number;
    maxWatts: number;
    timeInZone: number; // seconds
    percentageOfTime: number;
  }>;
  samples?: Array<{
    power?: number;
    cadence?: number;
    timestamp: string;
  }>;
}

interface ExpandablePowerCadenceProps {
  data: PowerCadenceData;
}

export const ExpandablePowerCadence: React.FC<ExpandablePowerCadenceProps> = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasPowerData = data.avgPower || data.maxPower || data.normalizedPower;
  const hasCadenceData = data.avgCadence || data.maxCadence;

  if (!hasPowerData && !hasCadenceData) {
    return null;
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getZoneColor = (zone: number): string => {
    const colors = [
      '#9CA3AF', // Zone 1 - Active Recovery (gray)
      '#3B82F6', // Zone 2 - Endurance (blue)
      '#10B981', // Zone 3 - Tempo (green)
      '#F59E0B', // Zone 4 - Threshold (orange)
      '#EF4444', // Zone 5 - VO2 Max (red)
      '#DC2626', // Zone 6 - Anaerobic (dark red)
      '#991B1B', // Zone 7 - Neuromuscular (darkest red)
    ];
    return colors[zone - 1] || '#9CA3AF';
  };

  return (
    <div className="border-t border-gray-700 pt-3 mt-3">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-300">⚡ Power & Cadence</span>
          {data.avgPower && (
            <span className="text-xs text-gray-400">
              {data.avgPower}W avg
            </span>
          )}
        </div>
        <span className="text-gray-400 text-sm">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {isExpanded && (
        <div className="mt-3 space-y-3">
          {/* Power Stats */}
          {hasPowerData && (
            <div>
              <div className="text-xs text-gray-400 font-medium mb-2">Power Metrics</div>
              <div className="grid grid-cols-3 gap-2">
                {data.avgPower && (
                  <div className="bg-gray-800 rounded-lg p-2">
                    <div className="text-xs text-gray-400 mb-1">Average</div>
                    <div className="text-sm font-semibold text-white">{data.avgPower}W</div>
                  </div>
                )}
                {data.maxPower && (
                  <div className="bg-gray-800 rounded-lg p-2">
                    <div className="text-xs text-gray-400 mb-1">Max</div>
                    <div className="text-sm font-semibold text-white">{data.maxPower}W</div>
                  </div>
                )}
                {data.normalizedPower && (
                  <div className="bg-gray-800 rounded-lg p-2">
                    <div className="text-xs text-gray-400 mb-1">Normalized</div>
                    <div className="text-sm font-semibold text-white">{data.normalizedPower}W</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cadence Stats */}
          {hasCadenceData && (
            <div>
              <div className="text-xs text-gray-400 font-medium mb-2">Cadence</div>
              <div className="grid grid-cols-2 gap-2">
                {data.avgCadence && (
                  <div className="bg-gray-800 rounded-lg p-2">
                    <div className="text-xs text-gray-400 mb-1">Average</div>
                    <div className="text-sm font-semibold text-white">{data.avgCadence} rpm</div>
                  </div>
                )}
                {data.maxCadence && (
                  <div className="bg-gray-800 rounded-lg p-2">
                    <div className="text-xs text-gray-400 mb-1">Max</div>
                    <div className="text-sm font-semibold text-white">{data.maxCadence} rpm</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Power Zone Distribution */}
          {data.powerZones && data.powerZones.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-gray-400 font-medium mb-2">Time in Power Zones</div>
              {data.powerZones.map((zone) => (
                <div key={zone.zone} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-300">
                      Zone {zone.zone}: {zone.name} ({zone.minWatts}-{zone.maxWatts}W)
                    </span>
                    <span className="text-gray-400">
                      {formatTime(zone.timeInZone)} ({zone.percentageOfTime.toFixed(1)}%)
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        width: `${zone.percentageOfTime}%`,
                        backgroundColor: getZoneColor(zone.zone)
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

